import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Sparkles } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { messageHandler } from '../services/message-handler.service';
import { config } from '../lib/config';
import { Form, FormField, FormLabel, FormInput, Button } from '../components/ui';
import CompanyInfo from '../components/auth/CompanyInfo';

const loginSchema = z.object({
  username: z.string().min(1, 'Usuário é obrigatório'),
  password: z.string().min(1, 'Senha é obrigatória'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error, clearError, requiresTwoFactor } = useAuthStore(state => ({
    login: state.login,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    clearError: state.clearError,
    requiresTwoFactor: state.requiresTwoFactor,
  }));

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
      rememberMe: false,
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    } else if (requiresTwoFactor) {
      navigate('/2fa-verify', { replace: true });
    }
  }, [isAuthenticated, requiresTwoFactor, navigate]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data.username, data.password);
    } catch (err: any) {
      messageHandler.error(err);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-50 dark:bg-gray-950">
      {/* Left side - Brand section */}
      <div className="relative w-full md:w-1/2 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-950 dark:from-primary-800 dark:via-primary-900 dark:to-gray-950 flex flex-col justify-center items-center p-8 md:p-16 overflow-hidden">
        {/* Decorative gradient orbs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary-500/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />

        <div className="relative max-w-md w-full animate-fade-in-up">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <span className="text-white/90 text-sm font-medium tracking-wide uppercase">Inteligência Artificial</span>
          </div>

          <img
            className="mx-auto h-auto w-[180px] max-w-full sm:w-[220px]"
            src="/logo_branco.png"
            alt={config.name}
          />
          <h1 className="mt-8 text-3xl font-bold text-white text-center md:text-left tracking-tight">
            {config.description}
          </h1>
          <p className="mt-4 text-primary-100/80 text-lg text-center md:text-left leading-relaxed">
            Plataforma completa para gerenciamento de assistentes virtuais e automação de processos
            com inteligência artificial.
          </p>

          <CompanyInfo />
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-16 bg-gradient-to-br from-white to-primary-50/30 dark:from-gray-900 dark:to-primary-950/10">
        <div className="max-w-md w-full space-y-8 glass-card rounded-2xl p-8 md:p-10 animate-scale-in">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center tracking-tight">
              Acesse sua conta
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center">
              Entre com suas credenciais para acessar o sistema
            </p>
          </div>

          <Form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
            <FormField>
              <FormLabel htmlFor="username" required>
                Usuário
              </FormLabel>
              <FormInput
                {...register('username')}
                error={!!errors.username}
                validationError={errors.username?.message}
              />
            </FormField>

            <FormField>
              <FormLabel htmlFor="password" required>
                Senha
              </FormLabel>
              <FormInput
                type="password"
                {...register('password')}
                error={!!errors.password}
                validationError={errors.password?.message}
              />
            </FormField>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('rememberMe')}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500/40 transition-colors"
                />
                <label
                  htmlFor="rememberMe"
                  className="ml-2 block text-sm text-gray-600 dark:text-gray-300"
                >
                  Lembrar-me
                </label>
              </div>

              <div className="text-sm">
                <a
                  href="/forgot-password"
                  className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 transition-colors"
                >
                  Esqueceu a senha?
                </a>
              </div>
            </div>

            <Button type="submit" isLoading={isLoading} fullWidth size="lg">
              Entrar
            </Button>

            <div className="text-center">
              <a
                href="/register"
                className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 transition-colors"
              >
                Não tem uma conta? Cadastre-se
              </a>
            </div>
          </Form>

          <div className="mt-6 pt-6 border-t border-gray-200/60 dark:border-gray-800/60">
            <div className="text-center text-xs text-gray-400 dark:text-gray-500">
              <p>{config.company.copyright}</p>
              <p className="mt-1">Versão {config.version}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
